import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, apiError, MONTHS, STAGE_LABELS } from "../adminApi";
import { Panel, ScoreBar, Stat } from "../components/Bits";
import { AutomationPanel } from "../components/AutomationPanel";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([adminApi.get("/crm/stats"), adminApi.get("/crm/activities?limit=12")])
      .then(([statsRes, activityRes]) => {
        setStats(statsRes.data);
        setActivities(activityRes.data.activities);
      })
      .catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <p className="text-sm text-terra" data-testid="dashboard-error">{error}</p>;
  if (!stats) return <p className="text-sm text-cream/40" data-testid="dashboard-loading">Loading…</p>;

  const stageRows = Object.entries(stats.by_stage).filter(([, count]) => count > 0);

  return (
    <div className="space-y-7" data-testid="admin-dashboard">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Growth Desk</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          Pipeline at a glance
        </h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Target accounts" value={stats.total_accounts.toLocaleString()}
              hint="From public Form 5500 filings" testid="stat-accounts" />
        <Stat label="High fit (70+)" value={stats.high_fit.toLocaleString()}
              hint="Score threshold for outreach" testid="stat-high-fit" />
        <Stat label="Renewals ≤ 120 days" value={stats.renewals_next_120_days.toLocaleString()}
              hint="Plan anniversary approaching" testid="stat-renewals" />
        <Stat label="Open tasks" value={stats.open_tasks} hint={`${stats.contacts} contacts on file`}
              testid="stat-tasks" />
      </div>

      {stats.peo_signal ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <Stat label="On a PEO"
                value={(stats.peo_signal.confirmed + stats.peo_signal.likely).toLocaleString()}
                hint="Confirmed or likely, from public filings" testid="stat-on-peo" />
          <Stat label="PEO confirmed" value={stats.peo_signal.confirmed.toLocaleString()}
                hint="A PEO is named on the filing" testid="stat-peo-confirmed" />
          <Stat label="Files their own plan" value={stats.peo_signal.unknown.toLocaleString()}
                hint="No PEO signal — group health prospect" testid="stat-peo-unknown" />
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <Panel title="Top fit accounts" testid="panel-top-accounts">
          <ul className="divide-y divide-cream/10">
            {stats.top_accounts.map((account) => (
              <li key={account.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <Link
                    to={`/admin/accounts/${account.id}`}
                    className="block truncate text-sm text-cream transition-colors duration-200 hover:text-gold"
                    data-testid={`top-account-${account.id}`}
                  >
                    {account.name}
                  </Link>
                  <p className="mt-0.5 text-xs text-cream/40">
                    {account.city} · {account.participants} covered ·{" "}
                    {account.renewal_month ? `renews ${MONTHS[account.renewal_month - 1]}` : "renewal unknown"}
                  </p>
                </div>
                <ScoreBar score={account.score} />
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="By segment" testid="panel-segments">
          <ul className="space-y-3">
            {Object.entries(stats.by_segment).map(([segment, count]) => (
              <li key={segment} className="flex items-center justify-between text-sm">
                <span className="text-cream/70">{segment}</span>
                <span className="font-mono text-cream">{count.toLocaleString()}</span>
              </li>
            ))}
          </ul>

          <h3 className="mt-7 font-mono text-[10px] uppercase tracking-[0.28em] text-gold/80">
            By stage
          </h3>
          <ul className="mt-4 space-y-3">
            {stageRows.map(([stage, count]) => (
              <li key={stage} className="flex items-center justify-between text-sm">
                <span className="text-cream/70">{STAGE_LABELS[stage]}</span>
                <span className="font-mono text-cream">{count.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Recent activity" testid="panel-activity">
          {activities.length === 0 ? (
            <p className="text-sm text-cream/40">
              No activity yet. Log a call or note on an account to start the record.
            </p>
          ) : (
            <ul className="divide-y divide-cream/10">
              {activities.map((activity) => (
                <li key={activity.id} className="py-3">
                  <p className="text-sm text-cream/85">{activity.body}</p>
                  <p className="mt-1 text-xs text-cream/40">
                    {activity.account_name} · {activity.type} ·{" "}
                    {new Date(activity.created_at).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <AutomationPanel />

      {stats.last_ingest ? (
        <p className="text-xs text-cream/35" data-testid="last-ingest-note">
          Last data load: {stats.last_ingest.status} · {stats.last_ingest.total?.toLocaleString() || 0}{" "}
          filings processed · {new Date(stats.last_ingest.started_at).toLocaleString()}
        </p>
      ) : null}
    </div>
  );
}
